import React, {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
} from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GlobalContext = createContext(null);

const CTX_LS = "zae_ctx";
const lsKey = (uid) => `${CTX_LS}_${uid}`;
function readCtx(uid) {
  try { return JSON.parse(localStorage.getItem(uid ? lsKey(uid) : CTX_LS)) || {}; } catch { return {}; }
}

export const MESES_LARGOS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export function GlobalProvider({ children }) {
  const { user } = useAuth();
  const _persisted = {};
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [structureKey, setStructureKeyState] = useState(_persisted.structureKey || null);
  const [node, setNode] = useState(_persisted.node || null); // { id, name, label, path: [{id,name,label}] }
  const [period, setPeriod] = useState(() => { const d = new Date(); return { year: d.getFullYear(), monthIndex: d.getMonth() }; });

  const [ready, setReady] = useState(false); // selección restaurada (evita guardar antes de tiempo)

  useEffect(() => {
    if (user === null) return; // sesión verificándose: conservar la selección guardada
    setReady(false);
    if (!user) {
      setStructures([]);
      setStructureKeyState(null);
      setNode(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    // 1) Restauración inmediata desde el navegador (por usuario); 2) confirmación con la base de datos.
    const local = readCtx(user.id);
    if (local.structureKey) { setStructureKeyState(local.structureKey); setNode(local.node || null); }
    Promise.all([api.get("/context/structures"), api.get("/context/last-selection").catch(() => ({ data: null }))])
      .then(([{ data }, { data: last }]) => {
        if (!active) return;
        setStructures(data);
        const ok = (k) => k && data.some((s) => s.key === k);
        if (last?.valid && ok(last.selection.structure_key)) {
          setStructureKeyState(last.selection.structure_key);
          setNode(last.selection.node);
        } else if (last?.saved && !last.valid) {
          setStructureKeyState(data[0]?.key || null);
          setNode(null);
          toast.warning("Tu última selección ya no está disponible. Elige una planta o sucursal en la estructura organizacional.");
          window.dispatchEvent(new Event("zae:open-structure"));
        } else if (!ok(local.structureKey)) {
          setStructureKeyState(data[0]?.key || null);
          setNode(null); // "Toda la estructura" por defecto (regla de acumulación)
        }
        setReady(true);
        setLoading(false);
      }).catch(() => setLoading(false));
    return () => { active = false; };
  }, [user]);

  // Persistir la selección por usuario: navegador al instante y base de datos.
  useEffect(() => {
    if (!ready || !user || !structureKey) return;
    try { localStorage.setItem(lsKey(user.id), JSON.stringify({ structureKey, node })); } catch { /* ignore */ }
    const t = setTimeout(() => {
      api.put("/context/last-selection", { structure_key: structureKey, node }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [ready, user, structureKey, node]);

  const currentStructure = useMemo(
    () => structures.find((s) => s.key === structureKey) || null,
    [structures, structureKey]
  );

  const setStructureKey = useCallback((key) => {
    setStructureKeyState(key);
    setNode(null); // al cambiar de estructura => Toda la estructura autorizada
  }, []);

  const selectEntireStructure = useCallback(() => setNode(null), []);

  const drillInto = useCallback((card) => {
    setNode((prev) => {
      const base = prev?.path || [];
      const step = { id: card.id, name: card.name, label: card.label };
      return { id: card.id, name: card.name, label: card.label, path: [...base, step] };
    });
  }, []);

  const goToPathIndex = useCallback((index) => {
    setNode((prev) => {
      if (!prev) return prev;
      const path = prev.path.slice(0, index + 1);
      const last = path[path.length - 1];
      return { id: last.id, name: last.name, label: last.label, path };
    });
  }, []);

  const periodRange = useMemo(() => {
    const { year, monthIndex } = period;
    const start = `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const end = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${lastDay}`;
    return { start, end, label: `${MESES_LARGOS[monthIndex]} ${year}` };
  }, [period]);

  const value = {
    structures, loading, structureKey, setStructureKey, currentStructure,
    node, setNode, drillInto, goToPathIndex, selectEntireStructure,
    period, setPeriod, periodRange,
  };

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
}

export function useGlobal() {
  return useContext(GlobalContext);
}
